import { useEffect } from "react";
import { useTheme } from "@mui/material/styles";
export const useDrawingEvents = ({ svgCanvasRef, svgCanvasInstanceRef, tool, brushWidth, handleAddWall, handleAddDoor, handleAddWindow, handleAddTextAtPosition, textValue, }) => {
    const theme = useTheme();
    useEffect(() => {
        const canvas = svgCanvasInstanceRef.current;
        const containerElement = svgCanvasRef.current;
        if (!canvas || !containerElement)
            return;
        let isDrawing = false;
        let currentPath = null;
        let points = [];
        const getSVGPoint = (e) => {
            var _a, _b, _c, _d, _e, _f;
            try {
                const clientX = "touches" in e ? (_a = e.touches[0]) === null || _a === void 0 ? void 0 : _a.clientX : e.clientX;
                const clientY = "touches" in e ? (_b = e.touches[0]) === null || _b === void 0 ? void 0 : _b.clientY : e.clientY;
                if (clientX == null || clientY == null)
                    return null;
                const rootSctm = (_d = (_c = canvas).getrootSctm) === null || _d === void 0 ? void 0 : _d.call(_c);
                if (rootSctm) {
                    return {
                        x: rootSctm.a * clientX + rootSctm.c * clientY + rootSctm.e,
                        y: rootSctm.b * clientX + rootSctm.d * clientY + rootSctm.f,
                    };
                }
                const svgRoot = (_f = (_e = canvas).getSvgRoot) === null || _f === void 0 ? void 0 : _f.call(_e);
                if (!svgRoot)
                    return null;
                const pt = svgRoot.createSVGPoint();
                pt.x = clientX;
                pt.y = clientY;
                const screenCTM = svgRoot.getScreenCTM();
                if (!screenCTM)
                    return null;
                const svgPoint = pt.matrixTransform(screenCTM.inverse());
                return { x: svgPoint.x, y: svgPoint.y };
            }
            catch (_g) {
                return null;
            }
        };
        const smoothPath = (points) => {
            if (points.length < 2)
                return "";
            let path = `M ${points[0].x} ${points[0].y}`;
            for (let i = 1; i < points.length; i++) {
                if (i === 1) {
                    path += ` L ${points[i].x} ${points[i].y}`;
                }
                else {
                    const prevPoint = points[i - 1];
                    const currPoint = points[i];
                    const midX = (prevPoint.x + currPoint.x) / 2;
                    const midY = (prevPoint.y + currPoint.y) / 2;
                    path += ` Q ${prevPoint.x} ${prevPoint.y} ${midX} ${midY}`;
                }
            }
            return path;
        };
        const handleMouseDown = (e) => {
            if (tool !== "draw")
                return;
            const point = getSVGPoint(e);
            if (!point)
                return;
            isDrawing = true;
            points = [point];
            try {
                const svgContent = canvas.getSvgContent();
                if (!svgContent)
                    return;
                currentPath = document.createElementNS("http://www.w3.org/2000/svg", "path");
                currentPath.setAttribute("id", canvas.getNextId());
                currentPath.setAttribute("d", `M ${point.x} ${point.y}`);
                currentPath.setAttribute("fill", "none");
                currentPath.setAttribute("stroke", theme.palette.primary.main);
                currentPath.setAttribute("stroke-width", brushWidth.toString());
                currentPath.setAttribute("stroke-linecap", "round");
                currentPath.setAttribute("stroke-linejoin", "round");
                svgContent.appendChild(currentPath);
                canvas.call("changed", [currentPath]);
            }
            catch (error) {
                console.error("Error starting draw:", error);
            }
        };
        const handleMouseMove = (e) => {
            if (tool !== "draw" || !isDrawing || !currentPath)
                return;
            const point = getSVGPoint(e);
            if (!point)
                return;
            points.push(point);
            if (points.length > 3) {
                const pathData = smoothPath(points);
                currentPath.setAttribute("d", pathData);
                canvas.call("changed", [currentPath]);
            }
            else if (points.length === 2) {
                currentPath.setAttribute("d", `M ${points[0].x} ${points[0].y} L ${points[1].x} ${points[1].y}`);
            }
        };
        const handleMouseUp = () => {
            if (tool !== "draw" || !isDrawing || !currentPath)
                return;
            isDrawing = false;
            if (points.length > 0 && currentPath) {
                const pathData = smoothPath(points);
                currentPath.setAttribute("d", pathData);
                canvas.call("changed", [currentPath]);
                canvas.addToSelection([currentPath], false);
            }
            currentPath = null;
            points = [];
        };
        const handleClick = (e) => {
            if (tool === "wall" || tool === "door" || tool === "window" || tool === "text") {
                try {
                    const point = getSVGPoint(e);
                    if (!point)
                        return;
                    e.preventDefault();
                    e.stopPropagation();
                    const { x, y } = point;
                    if (tool === "wall") {
                        const halfLength = 50;
                        handleAddWall({ x: x - halfLength, y }, { x: x + halfLength, y });
                    }
                    else if (tool === "door") {
                        handleAddDoor({ x, y });
                    }
                    else if (tool === "window") {
                        handleAddWindow({ x, y });
                    }
                    else if (tool === "text" && textValue.trim()) {
                        handleAddTextAtPosition({ x, y });
                    }
                }
                catch (error) {
                    console.error("Error handling click:", error);
                }
            }
        };
        containerElement.addEventListener("mousedown", handleMouseDown);
        containerElement.addEventListener("mousemove", handleMouseMove);
        containerElement.addEventListener("mouseup", handleMouseUp);
        containerElement.addEventListener("mouseleave", handleMouseUp);
        containerElement.addEventListener("touchstart", handleMouseDown);
        containerElement.addEventListener("touchmove", handleMouseMove);
        containerElement.addEventListener("touchend", handleMouseUp);
        containerElement.addEventListener("click", handleClick);
        return () => {
            containerElement.removeEventListener("mousedown", handleMouseDown);
            containerElement.removeEventListener("mousemove", handleMouseMove);
            containerElement.removeEventListener("mouseup", handleMouseUp);
            containerElement.removeEventListener("mouseleave", handleMouseUp);
            containerElement.removeEventListener("touchstart", handleMouseDown);
            containerElement.removeEventListener("touchmove", handleMouseMove);
            containerElement.removeEventListener("touchend", handleMouseUp);
            containerElement.removeEventListener("click", handleClick);
        };
    }, [
        tool,
        brushWidth,
        theme.palette.primary.main,
        svgCanvasRef,
        svgCanvasInstanceRef,
        handleAddWall,
        handleAddDoor,
        handleAddWindow,
        handleAddTextAtPosition,
        textValue,
    ]);
};
